import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CustomerOrderHeader {
    orderId: number;
    orderNumber: string;
    companyId: string;
    divisionId: string;
    customerId: string;
    customerName: string;
    status: number;
    orderMovingStatus: string;
    requestedDate: string;
    orderCreatedDate: string;
    grossValue: string;
    netAmount: string;
    vatAmount?: string;
    orderValue?: string;
    totalArticles?: number;
    totalWeight?: number;
    invoiceNumber: string;
    customerAddress?: string;
    userIdResponsible?: string;
    phoneNumber?: string;
    outstanding?: string;
    orderTypeId?: string;
}

export interface CustomerOrderLine {
    orderLineId: number;
    lineNo: string;
    itemNo: string;
    itemName: string;
    qty: number;
    rate?: number;
    lineAmount: number;
    netAmount: number;
    grossAmount: number;
    vat: number;
    size?: string;
    status: string;
}

export interface CustomerOrderLinePage {
    content: CustomerOrderLine[];
    pageNumber: number;
    pageSize: number;
    totalElement: number;
    totalPages: number;
    lastPage: boolean;
}

export interface CustomerOrderPage {
    content: CustomerOrderHeader[];
    pageNumber: number;
    pageSize: number;
    totalElement: number;
    totalPages: number;
}

export interface CustomerDisputeSummary {
    claimNumber: string;
    customerId: string;
    customerName: string;
    companyId: string;
    divisionId: string;
    orderNumber: string;
    invoiceNumber: string;
    status: string;
    claimType: string;
    totalClaimQty: number;
    createdAt: string;
}

export interface DisputeLine {
    id: number;
    orderLineId: number;
    itemNumber: string;
    itemName: string;
    orderedQty: number;
    claimedQty: number;
    approvedQty?: number;
    reason: string;
    resolutionType?: string;
}

export interface DisputeAttachment {
    id: number;
    claimLineId?: number;
    originalFileName: string;
    contentType: string;
    fileSize: number;
    uploadedAt?: string;
}

export interface DisputeStatusEvent {
    oldStatus?: string;
    newStatus: string;
    comment?: string;
    changedBy?: string;
    changedAt?: string;
}

export interface DisputeCreditNoteLink {
    creditNoteOrderNumber: string;
    creditNoteOrderType: string;
    createdAt?: string;
    createdBy?: string;
}

export interface CustomerDisputeDetail {
    claimNumber: string;
    orderNumber: string;
    invoiceNumber?: string;
    status: string;
    claimType: string;
    customerComment?: string;
    totalClaimQty?: number;
    totalClaimAmount?: number;
    createdAt?: string;
    updatedAt?: string;
    creditNoteOrderNumber?: string;
    creditNoteOrderType?: string;
    creditNoteCreatedAt?: string;
    creditNotes?: DisputeCreditNoteLink[];
    lines: DisputeLine[];
    attachments: DisputeAttachment[];
    timeline: DisputeStatusEvent[];
}

export interface InvoiceViewResponse {
    format: string;
    html?: string;
    contentBase64?: string;
}

@Injectable({ providedIn: 'root' })
export class StaffCustomerViewService {
    constructor(private http: HttpClient) {}

    private base(customerId: string): string {
        return `${environment.apiUrl}staff/customer-view/${encodeURIComponent(customerId)}`;
    }

    listOrders(
        customerId: string,
        params?: { statusGroup?: string; page?: number; pageSize?: number }
    ): Observable<CustomerOrderPage> {
        let httpParams = new HttpParams();
        if (params?.statusGroup) httpParams = httpParams.set('statusGroup', params.statusGroup);
        httpParams = httpParams.set('page', String(params?.page ?? 0));
        httpParams = httpParams.set('pageSize', String(params?.pageSize ?? 20));
        return this.http.get<CustomerOrderPage>(`${this.base(customerId)}/orders`, { params: httpParams });
    }

    getOrderDetail(
        customerId: string,
        orderNumber: string,
        company: string,
        division: string
    ): Observable<CustomerOrderHeader> {
        const params = new HttpParams().set('company', company).set('division', division);
        return this.http.get<CustomerOrderHeader>(
            `${this.base(customerId)}/orders/${encodeURIComponent(orderNumber)}`,
            { params }
        );
    }

    getOrderLines(
        customerId: string,
        orderNumber: string,
        company: string,
        division: string
    ): Observable<CustomerOrderLinePage> {
        const params = new HttpParams().set('company', company).set('division', division);
        return this.http.get<CustomerOrderLinePage>(
            `${this.base(customerId)}/orders/${encodeURIComponent(orderNumber)}/lines`,
            { params }
        );
    }

    listDisputes(customerId: string, status?: string): Observable<CustomerDisputeSummary[]> {
        let httpParams = new HttpParams();
        if (status) httpParams = httpParams.set('status', status);
        return this.http.get<CustomerDisputeSummary[]>(`${this.base(customerId)}/disputes`, { params: httpParams });
    }

    getDisputeDetail(customerId: string, claimNumber: string): Observable<CustomerDisputeDetail> {
        return this.http.get<CustomerDisputeDetail>(
            `${this.base(customerId)}/disputes/${encodeURIComponent(claimNumber)}`
        );
    }

    getInvoiceView(customerId: string, orderNumber: string): Observable<InvoiceViewResponse> {
        return this.http.get<InvoiceViewResponse>(
            `${this.base(customerId)}/orders/${encodeURIComponent(orderNumber)}/invoice-view`
        );
    }
}
